import { useContext, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useConfirmation from 'containers/confirmation/useConfirmation'
import { consumeConfirmResume } from 'utils/shopifyConfirmResume'

/**
 * Consumes `?shopify_reconnected=true&shop=…` on the Sources URL — set by the OAuth
 * redirect when an already-entitled org (live/paid-period subscription, Stripe/manual
 * plan) re-authorizes its store: expired offline token, scope re-auth, app reinstall
 * (#1676). Pure acknowledgement — no billing action, the entitled path deliberately
 * bypasses plan selection.
 *
 * Same one-shot rules as useShopifyStoreSwitch: params are consumed synchronously
 * (once-per-mount ref + URL strip) so an org switch / re-render can't re-fire the
 * dialog. No sessionStorage needed — acknowledging twice is harmless, unlike a switch.
 */
export default function useShopifyReconnected(refreshAccounts: (orgId: number) => void) {
  const history = useHistory()
  const location = useLocation()
  const { currentOrganization } = useContext(OrganizationsContext)
  const { confirm } = useConfirmation()
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    const params = new URLSearchParams(location.search)
    if (params.get('shopify_reconnected') !== 'true') return
    if (!currentOrganization.id) return
    const shop = params.get('shop') || ''

    handledRef.current = true
    const orgId = currentOrganization.id

    // Subscription-confirm re-auth hop (#1658/#1659): right after a purchase, the confirm
    // page bounces through OAuth to mint fresh credentials. By then the activation webhook
    // has usually made the connection active, so the callback routes here — but this is
    // NOT a user-initiated reconnect: resume the stashed confirm URL (payment success +
    // first-sync progress) instead of showing the reconnected dialog. consumeConfirmResume
    // is one-shot + TTL'd, so a stale stash can't hijack a genuine later reconnect.
    const resumeUrl = consumeConfirmResume()
    if (resumeUrl) {
      history.replace(resumeUrl)
      return
    }

    // Strip only the search, stay on the current route (see useShopifyStoreSwitch on why
    // navigating away here would unmount the hook mid-flight).
    history.replace(location.pathname)

    refreshAccounts(orgId)
    const storeName = shop ? `${shop}.myshopify.com` : 'Your Shopify store'
    confirm(`${storeName} was reconnected successfully. Your subscription is unchanged.`, {
      confirmLabel: 'OK',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, currentOrganization.id])
}
